import { NavigationEnd, Router } from '@angular/router';
import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { GuideService, TooltipOverlay } from '../../services/guide.service';
import { environment } from 'src/environments/environment';
import { Constant } from 'src/app/constant/constant';

@Component({
  selector: 'calsoft-step-guide',
  templateUrl: './step-guide.component.html',
  styleUrls: ['./step-guide.component.scss']
})
export class StepGuideComponent implements AfterViewInit, OnDestroy {

  sanitizedUrl: string = '';

  url = '';

  @ViewChild('tooltipBox') tooltipBox!: ElementRef;
  @ViewChild('sidePanelDesc') sidePanelDesc!: ElementRef;

  overlay: TooltipOverlay | null = null;
  notification: string | null = null;
  showDetailsModal = false;
  isTruncated = false;
  isStacked = false;
  private destroy$ = new Subject<void>();
  private destroyed = false;  // guard for all setTimeout callbacks

  cid: any;

  public serverPath = environment.commonImageApi + 'stepGuideImage/';


  public serverPathNew = environment.stepGuideImage + 'stepGuideImage/';

  constructor(public guide: GuideService, private router: Router) {

    let values = localStorage.getItem(Constant.enterPriseKey);

    this.cid = "&cid=" + values

    this.guide.overlay$.pipe(takeUntil(this.destroy$)).subscribe(o => {
      this.overlay = o;
      if (o) {
        this.isTruncated = !!o.step.text;
        // After render: fix layout helpers and clamp to viewport using real dimensions.
        // Guard with destroyed flag so callbacks don't fire on a destroyed component.
        setTimeout(() => {
          if (this.destroyed) return;
          this.calculatePanelLayout();
          this.checkTruncation();
          this.clampToViewport();
        }, 60);
      }
    });

    this.guide.notification$.pipe(takeUntil(this.destroy$)).subscribe(msg => {
      this.notification = msg;
      setTimeout(() => {
        if (this.destroyed) return;
        this.notification = null;
      }, 3000);
    });
    this.url = this.router.url || '';
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => { this.url = this.router.url || ''; });
  }

  ngAfterViewInit() {
    this.measureAndFix();
  }

  measureAndFix() {
    if (this.tooltipBox?.nativeElement) {
      const box = this.tooltipBox.nativeElement;
      this.guide.reposition(box.offsetWidth, box.offsetHeight);
      this.calculatePanelLayout();
    }
  }

  calculatePanelLayout() {
    if (!this.tooltipBox?.nativeElement) return;

    const box = this.tooltipBox.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const panelWidth = 250; // Based on SCSS
    const padding = 20;

    // Check if there is enough space on the right of the main panel
    // box.right is the distance from left edge of viewport to right edge of element
    const spaceOnRight = viewportWidth - box.right;

    this.isStacked = spaceOnRight < (panelWidth + padding);
  }

  next() { this.guide.next(); }
  prev() { this.guide.prev(); }
  skip() { this.guide.skip(); }

  get visibleSteps(): number[] {
    const total = this.guide.steps.length;
    const current = this.guide.currentIndex;
    const max = 10;

    if (total <= max) {
      const arr = [];
      for (let i = 0; i < total; i++) arr.push(i);
      return arr;
    }

    let start = Math.max(0, current - Math.floor(max / 2));
    let end = start + max;

    if (end > total) {
      end = total;
      start = Math.max(0, end - max);
    }

    const indices = [];
    for (let i = start; i < end; i++) {
      indices.push(i);
    }
    return indices;
  }


  @HostListener('window:resize')
  onResize() {
    if (this.overlay) {
      this.guide.reflow();
      this.checkTruncation();
      this.calculatePanelLayout();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDetails() {
    if (this.overlay?.step?.text) {
      this.showDetailsModal = !this.showDetailsModal;
    }
  }

  playVideo(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  checkTruncation() {
    if (!this.overlay?.step?.text) {
      this.isTruncated = false;
      return;
    }

    // Measure the actual height of the description element
    // Since we start with isTruncated = true, the CSS line-clamp is active.
    // If scrollHeight > clientHeight, it means content is being hidden/truncated.
    const descEl = this.sidePanelDesc?.nativeElement;
    if (descEl) {
      const needsMore = descEl.scrollHeight > descEl.clientHeight;
      this.isTruncated = needsMore;
    } else if (!this.destroyed) {
      // Retry once if element not yet in DOM — guarded against post-destroy firing
      setTimeout(() => { if (!this.destroyed) this.checkTruncation(); }, 80);
    }
  }

  sanitizeUrl(u: string): string {
    return (u || '')
      .replace(/^\/+/, '')
      .replace(/[?#&=]/g, '-')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

  /** Re-evaluates best placement using real rendered tooltip size. Updates local overlay only (no service re-emit). */
  private clampToViewport() {
    if (this.destroyed || !this.tooltipBox?.nativeElement || !this.overlay) return;
    const box = this.tooltipBox.nativeElement as HTMLElement;
    const w = box.offsetWidth;
    const h = box.offsetHeight;
    if (!w || !h) return;

    const pad = 15;
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = this.overlay.targetRect;

    // If no target rect (center position), just clamp
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      const top = Math.max(pad, Math.min(this.overlay.top, vh - h - pad));
      const left = Math.max(pad, Math.min(this.overlay.left, vw - w - pad));
      if (top !== this.overlay.top || left !== this.overlay.left) {
        this.overlay = { ...this.overlay, top, left };
      }
      return;
    }

    // Available space on each side (using real rendered dimensions)
    const spaceRight  = vw - rect.right - margin;
    const spaceLeft   = rect.left - margin;
    const spaceBottom = vh - rect.bottom - margin;
    const spaceTop    = rect.top - margin;

    const preferred = this.overlay.step?.position || 'auto';
    const order = preferred !== 'auto'
      ? [preferred, 'bottom', 'right', 'top', 'left']
      : ['bottom', 'right', 'top', 'left'];

    const fits: Record<string, boolean> = {
      right:  spaceRight  >= w,
      left:   spaceLeft   >= w,
      bottom: spaceBottom >= h,
      top:    spaceTop    >= h
    };

    const pos = order.find(p => fits[p]) || 'bottom';

    let top = 0;
    let left = 0;

    switch (pos) {
      case 'bottom': top = rect.bottom + margin; left = rect.left + rect.width / 2 - w / 2; break;
      case 'top':    top = rect.top - h - margin; left = rect.left + rect.width / 2 - w / 2; break;
      case 'right':  top = rect.top + rect.height / 2 - h / 2; left = rect.right + margin; break;
      case 'left':   top = rect.top + rect.height / 2 - h / 2; left = rect.left - w - margin; break;
    }

    // Final viewport clamp
    left = Math.max(pad, Math.min(left, vw - w - pad));
    top  = Math.max(pad, Math.min(top,  vh - h - pad));

    // Recalculate arrow so it still points at the target element after repositioning
    const arrowSize = 10;
    let arrowLeft = 0;
    let arrowTop = 0;
    const targetCenterX = rect.left + rect.width / 2;
    const targetCenterY = rect.top + rect.height / 2;
    if (pos === 'top' || pos === 'bottom') {
      arrowLeft = Math.max(10, Math.min(w - 20 - arrowSize, targetCenterX - left - arrowSize));
    } else {
      arrowTop = Math.max(10, Math.min(h - 20 - arrowSize, targetCenterY - top - arrowSize));
    }

    this.overlay = { ...this.overlay, top, left, position: pos, arrowPosition: { left: arrowLeft, top: arrowTop } };
  }
}
